import {
  Card as CardUI,
  CardContent,
  CardHeadlineHeader,
  CardProps,
  CardTagHeader,
} from '@portals/core/src/components/Card/Card';
import { Pill } from '@portals/core/src/components/Pill/Pill';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import type { CustomVariant as TypographyVariants } from '@portals/core/src/components/Typography/Typography.types.d';
import { ArrowRight } from '@portals/icons/src/ArrowRight/ArrowRight';
import React from 'react';

import { ProductCard as ProductCardModel } from '../../../models';
import { useLinkProps } from '../../hooks/useLinkProps';
import { AspectMedia, MEDIA_SIZES as ASPECT_MEDIA_SIZES } from '../AspectMedia/AspectMedia';
import { TopInfoBox } from '../TopInfoBox/TopInfoBox';

export type ProductCardVariants = Extract<
  CardProps['variant'],
  | 'product:heroThirdLeft'
  | 'product:heroThirdRight'
  | 'product:half'
  | 'product:halfSquare'
  | 'product:third'
  | 'product:thirdSquare'
  | 'product:fourth'
>;

export interface Props {
  node: ProductCardModel;
  variant?: ProductCardVariants;
}

const REQUIRES_16x9_MEDIA: { [key in ProductCardVariants]-?: boolean | { xs: string; sm: string; lg: string } } = {
  'product:heroThirdLeft': true,
  'product:heroThirdRight': true,
  'product:half': true,
  'product:halfSquare': { xs: 'block', sm: 'none', lg: 'none' },
  'product:third': true,
  'product:thirdSquare': { xs: 'block', sm: 'none', lg: 'none' },
  'product:fourth': true,
};
const REQUIRES_4x3_MEDIA: { [key in ProductCardVariants]-?: boolean } = {
  'product:heroThirdLeft': false,
  'product:heroThirdRight': false,
  'product:half': false,
  'product:halfSquare': false,
  'product:third': false,
  'product:thirdSquare': false,
  'product:fourth': false,
};
const REQUIRES_1x1_MEDIA: { [key in ProductCardVariants]-?: boolean | { xs: string; sm: string; lg: string } } = {
  'product:heroThirdLeft': false,
  'product:heroThirdRight': false,
  'product:half': false,
  'product:halfSquare': { xs: 'none', sm: 'block', lg: 'block' },
  'product:third': false,
  'product:thirdSquare': { xs: 'none', sm: 'block', lg: 'block' },
  'product:fourth': false,
};

const MEDIA_SIZES: { [key in ProductCardVariants]-?: string } = {
  'product:heroThirdLeft': ASPECT_MEDIA_SIZES.lg,
  'product:heroThirdRight': ASPECT_MEDIA_SIZES.lg,
  'product:half': ASPECT_MEDIA_SIZES.md,
  'product:halfSquare': ASPECT_MEDIA_SIZES.md,
  'product:third': ASPECT_MEDIA_SIZES.sm,
  'product:thirdSquare': ASPECT_MEDIA_SIZES.sm,
  'product:fourth': ASPECT_MEDIA_SIZES.sm,
};

const getTypographyVariant = (variant: ProductCardVariants, subline: boolean): TypographyVariants => {
  return {
    'product:heroThirdLeft': subline ? 'h4' : 'h3',
    'product:heroThirdRight': subline ? 'h4' : 'h3',
    'product:half': 'h4',
    'product:halfSquare': 'h4',
    'product:third': 'h5',
    'product:thirdSquare': 'h5',
    'product:fourth': 'h5',
  }[variant] as TypographyVariants;
};

export const ProductCard: React.FunctionComponent<Props> = ({ node, variant = 'product:third' }: Props) => {
  const { linkProps, anchorProps } = useLinkProps(node.link[0]);

  const display_16x9 = REQUIRES_16x9_MEDIA[variant];
  const display_1x1 = REQUIRES_1x1_MEDIA[variant];

  return (
    <CardUI id={`${node.__contentType}.${node.id}`} variant={variant} {...linkProps}>
      {node.media?.media_16x9 && display_16x9 ? (
        <AspectMedia
          media={node.media.media_16x9}
          aspectWidth={16}
          aspectHeight={9}
          mediaSizes={MEDIA_SIZES[variant]}
          sx={typeof display_16x9 === 'object' ? { display: display_16x9 } : {}}
        />
      ) : null}
      {node.media?.media_4x3 && REQUIRES_4x3_MEDIA[variant] ? (
        <AspectMedia media={node.media.media_4x3} aspectWidth={3} aspectHeight={3} mediaSizes={MEDIA_SIZES[variant]} />
      ) : null}
      {node.media?.media_1x1 && display_1x1 ? (
        <AspectMedia
          media={node.media.media_1x1}
          aspectWidth={1}
          aspectHeight={1}
          mediaSizes={MEDIA_SIZES[variant]}
          sx={typeof display_1x1 === 'object' ? { display: display_1x1 } : {}}
        />
      ) : null}

      {!!node.link?.length && (
        <CardHeadlineHeader>
          <Typography
            variant={getTypographyVariant(variant, !!node.subline)}
            component="a"
            {...anchorProps}
            innerPadding="both"
          >
            {node.link[0].text}
            <ArrowRight sx={{ marginLeft: 2, verticalAlign: 'middle' }} fontSize="medium" />
          </Typography>
        </CardHeadlineHeader>
      )}

      <CardContent>
        {node.subline && (
          <Typography variant="h6" component="div" color="green.main" innerPadding="both">
            {node.subline.text}
          </Typography>
        )}
        {node.teaserText && (
          <Typography variant="body2" component="p" color="text.secondary" innerPadding="both">
            {node.teaserText}
          </Typography>
        )}
        {node.benefits && <TopInfoBox node={{ ...node.benefits, size: 'small' }} />}
      </CardContent>
      <CardTagHeader>
        {node.tags &&
          node.displayTags &&
          node.tags.map((tag) => <Pill key={tag.id} label={tag.name} sx={{ marginRight: 3 }} />)}
      </CardTagHeader>
    </CardUI>
  );
};
